import React, { createContext, useContext, useState, useEffect } from "react";
import purchaseService from "../api/services/purchaseService";

// Create context
const PurchaseContext = createContext();

// Custom hook for using this context
export const usePurchase = () => useContext(PurchaseContext);

// Provider component
export const PurchaseProvider = ({ children }) => {
  // State for purchase bills
  const [bills, setBills] = useState([]);
  const [loadingBills, setLoadingBills] = useState(false);
  const [billsError, setBillsError] = useState(null);

  // State for purchase orders
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState(null);

  // State for requisitions
  const [requisitions, setRequisitions] = useState([]);
  const [loadingRequisitions, setLoadingRequisitions] = useState(false);
  const [requisitionsError, setRequisitionsError] = useState(null);

  // Load bills from API
  const loadBills = async () => {
    setLoadingBills(true);
    setBillsError(null);
    try {
      const data = await purchaseService.getBills();
      setBills(data);
    } catch (error) {
      console.error("Error loading bills:", error);
      setBillsError("Failed to load purchase bills");
    } finally {
      setLoadingBills(false);
    }
  };

  // Load purchase orders from API
  const loadPurchaseOrders = async () => {
    setLoadingOrders(true);
    setOrdersError(null);
    try {
      const data = await purchaseService.getPurchaseOrders();
      setPurchaseOrders(data);
    } catch (error) {
      console.error("Error loading purchase orders:", error);
      setOrdersError("Failed to load purchase orders");
    } finally {
      setLoadingOrders(false);
    }
  };

  // Load requisitions from API
  const loadRequisitions = async () => {
    setLoadingRequisitions(true);
    setRequisitionsError(null);
    try {
      const data = await purchaseService.getRequisitions();
      setRequisitions(data);
    } catch (error) {
      console.error("Error loading requisitions:", error);
      setRequisitionsError("Failed to load requisitions");
    } finally {
      setLoadingRequisitions(false);
    }
  };

  // Create a new bill
  const createBill = async (billData) => {
    try {
      const newBill = await purchaseService.createBill(billData);
      setBills([...bills, newBill]);
      return newBill;
    } catch (error) {
      console.error("Error creating bill:", error);
      throw error;
    }
  };

  // Update a bill
  const updateBill = async (id, billData) => {
    try {
      const updatedBill = await purchaseService.updateBill(id, billData);
      setBills(bills.map((bill) => (bill.id === id ? updatedBill : bill)));
      return updatedBill;
    } catch (error) {
      console.error("Error updating bill:", error);
      throw error;
    }
  };

  // Delete a bill
  const deleteBill = async (id) => {
    try {
      await purchaseService.deleteBill(id);
      setBills(bills.filter((bill) => bill.id !== id));
    } catch (error) {
      console.error("Error deleting bill:", error);
      throw error;
    }
  };

  // Context value to be provided
  const value = {
    // Bills data and methods
    bills,
    loadingBills,
    billsError,
    loadBills,
    createBill,
    updateBill,
    deleteBill,

    // Purchase orders data and methods
    purchaseOrders,
    loadingOrders,
    ordersError,
    loadPurchaseOrders,

    // Requisitions data and methods
    requisitions,
    loadingRequisitions,
    requisitionsError,
    loadRequisitions,
  };

  return (
    <PurchaseContext.Provider value={value}>
      {children}
    </PurchaseContext.Provider>
  );
};

export default PurchaseContext;
